import CompliancePage, {
  CompliancePageContent,
} from '@comp/compliance/compliance-template'

const en = (text: string) => ({ en: text, tr: '' })

const content: CompliancePageContent = {
  path: '/compliance/gdpr',
  bilingual: false,
  seoTitle: en('GDPR Compliance: Identity & Access Controls | Monofor'),
  seoDescription: en(
    'Implement GDPR Article 32 security measures with Monofor: access control, MFA, privileged access management, and exportable evidence of who accessed personal data.'
  ),
  heroEyebrow: en('GDPR compliance'),
  heroTitle: en('Article 32, enforced at the identity layer.'),
  heroBody: en(
    'GDPR requires technical measures that ensure only the right people access personal data, and proof that the measures work. Monofor enforces authentication, least privilege, and privileged access discipline, then produces the audit trail your DPO and supervisory authority expect.'
  ),
  contextHeading: en('Where GDPR meets identity'),
  contextBody: en(
    'The GDPR’s security obligations concentrate in Article 32: controllers and processors must implement appropriate technical and organizational measures, explicitly including the ability to ensure ongoing confidentiality and integrity of processing systems. Article 5(1)(f) makes integrity and confidentiality a processing principle, and Article 25 requires data protection by design and by default: access limited to what each purpose needs.\n\nSupervisory authorities read these articles through an identity lens: who can reach personal data, how they authenticate, how administrator access is controlled, and whether the organization can reconstruct access after the fact. Breach investigations under Articles 33-34 start with exactly that reconstruction. An identity platform is where these controls either exist and produce evidence, or don’t.'
  ),
  requirementsHeading: en('Obligation to control, mapped.'),
  requirementsBody: en(
    'The identity-related GDPR obligations, and the Monofor capability that enforces and evidences each one.'
  ),
  requirements: [
    {
      requirement: en('Confidentiality of processing (Art. 32(1)(b))'),
      detail: en(
        'Systems processing personal data must remain confidential: access restricted to authenticated, authorized users.'
      ),
      capability: en(
        'Monosign centralizes authentication with SSO and MFA on every login, including phishing-resistant passkeys, so personal-data systems are never one password away from exposure.'
      ),
      productHref: '/sign',
      productName: 'Monosign',
    },
    {
      requirement: en('Data protection by design & default (Art. 25)'),
      detail: en(
        'Access should default to the minimum each role and purpose requires.'
      ),
      capability: en(
        'Role-based provisioning grants access by job function, joiner-mover-leaver automation revokes it the day roles change, and separation-of-duties policies block toxic permission combinations before they happen.'
      ),
      productHref: '/sync/governance',
      productName: 'Monosync Governance',
    },
    {
      requirement: en('Control of admin access to personal data'),
      detail: en(
        'Database, server, and application administrators are the highest-risk access path to personal data.'
      ),
      capability: en(
        'Monopam vaults privileged credentials, brokers admin sessions in the browser, records them with keystroke transcripts, and grants elevation just-in-time behind approvals: no standing superuser access.'
      ),
      productHref: '/pam',
      productName: 'Monopam',
    },
    {
      requirement: en('Regular testing & evaluation (Art. 32(1)(d))'),
      detail: en(
        'Measures must be regularly tested, assessed, and evaluated for effectiveness.'
      ),
      capability: en(
        'Scheduled access reviews certify that entitlements still match roles, with manager attestation and exportable results: a recurring, documented evaluation of your access controls.'
      ),
      productHref: '/sync/governance',
      productName: 'Monosync Governance',
    },
    {
      requirement: en('Breach detection & notification (Arts. 33-34)'),
      detail: en(
        'A 72-hour notification window leaves no time to reconstruct access from scattered logs.'
      ),
      capability: en(
        'Tamper-evident logs of every authentication, elevation, and entitlement change, plus privileged session recordings, give your incident team the who-what-when in minutes.'
      ),
      productHref: '/pam/session-recording',
      productName: 'Monopam Session Recording',
    },
    {
      requirement: en('Data residency & transfers (Ch. V)'),
      detail: en(
        'Cross-border transfer rules make many EU organizations keep identity data inside their own perimeter.'
      ),
      capability: en(
        'Monofor deploys fully self-hosted in your EU datacenter or cloud tenant at the same price as SaaS; identity data, logs, and recordings stay under your jurisdiction.'
      ),
      productHref: '/sign',
      productName: 'Monosign',
    },
  ],
  faqs: [
    {
      q: en('Does using Monofor make us GDPR compliant?'),
      a: en(
        'No product makes an organization GDPR compliant; the regulation spans legal bases, data subject rights, contracts, and governance. Monofor implements the identity-related technical measures of Articles 25 and 32 and produces the evidence for them, which is typically a substantial part of what supervisory authorities examine on the security side.'
      ),
    },
    {
      q: en('Is Monofor itself a data processor under GDPR?'),
      a: en(
        'It depends on deployment. Self-hosted, Monofor is software running on your infrastructure; you remain in full control and no personal data flows to us. In SaaS deployments Monofor acts as a processor with a standard DPA.'
      ),
    },
    {
      q: en('How does Monofor help with data subject requests?'),
      a: en(
        'Access logs and entitlement history let you answer which systems held a subject’s data and who accessed them. Lifecycle automation also supports erasure workflows by revoking and documenting access as accounts are removed.'
      ),
    },
    {
      q: en('What about KVKK, DORA, or NIS2?'),
      a: en(
        'The same identity controls serve overlapping frameworks. See our dedicated KVKK, DORA, and NIS2 pages for the framework-specific mappings; evidence is produced once and reused across audits.'
      ),
    },
  ],
}

type Props = { locale: string }

export default function GdprCompliancePage({ locale }: Props) {
  return <CompliancePage content={content} locale={locale} />
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
